import React from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Search, User, Heart, ChevronDown, Grid3X3, PhoneCall } from "lucide-react";
import { useCart } from "@/contexts/CartContext";

const G = "#FFB800";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);

export default function DesktopHeader({ searchQ, setSearchQ, handleSearch, megaOpen, setMegaOpen, isAuthenticated, isCustomer, logout, itemCount, onAuthOpen }) {
  const showSignIn = !(isAuthenticated && isCustomer);
  const { openDrawer } = useCart();

  return (
    <div className="hidden md:flex items-center flex-1 min-w-0 gap-1 xl:gap-2">
      {/* All Categories */}
      <button onClick={() => setMegaOpen(prev => !prev)}
        data-testid="mega-menu-trigger"
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-bold transition-all shrink-0 hover:bg-black/5"
        style={{ color: megaOpen ? G : "rgba(15,15,20,0.88)" }}>
        <Grid3X3 className="w-4 h-4" strokeWidth={2.5} />
        <span className="hidden xl:inline">All Categories</span>
        <span className="xl:hidden">All</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${megaOpen ? "rotate-180" : ""}`} strokeWidth={2.5} />
      </button>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex items-center flex-1 min-w-0 mx-1">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 nc-glass-search-icon" />
          <Input data-testid="search-input"
            className="nc-glass-search-input pl-9 w-full h-9 rounded-full text-sm"
            placeholder="Search products..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
        </div>
      </form>

      {/* Sign In */}
      {showSignIn && (
        <button onClick={onAuthOpen} data-testid="signin-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold rounded-full transition-all active:scale-[0.97] shrink-0 whitespace-nowrap hover:brightness-110"
          style={{ background: G, color: "#08080f", boxShadow: "0 4px 14px -4px rgba(255,184,0,0.55)" }}>
          <User className="w-4 h-4" /> <span>Sign In</span>
        </button>
      )}

      {/* Action Icons */}
      <div className="flex items-center shrink-0 gap-1">
        <Link to="/track" data-testid="track-order-link"
          className="hidden xl:block px-2 py-1.5 text-sm font-bold whitespace-nowrap transition-colors rounded-full nc-glass-link">
          Track Order
        </Link>
        <Link to="/wishlist" data-testid="wishlist-link"
          className="w-9 h-9 rounded-full flex items-center justify-center nc-glass-icon hover:bg-black/5">
          <Heart className="w-[18px] h-[18px]" />
        </Link>
        <button type="button" onClick={openDrawer} data-testid="cart-link-desktop"
          className="w-9 h-9 rounded-full flex items-center justify-center relative nc-glass-icon hover:bg-black/5">
          <ShoppingCart className="w-[18px] h-[18px]" />
          {itemCount > 0 && (
            <Badge className="absolute -top-0.5 -right-0.5 h-[18px] min-w-[18px] flex items-center justify-center text-[10px] px-1 rounded-full z-10 font-bold"
              style={{ background: G, color: "#08080f" }} data-testid="cart-badge-desktop">
              {itemCount}
            </Badge>
          )}
        </button>
        {isAuthenticated && isCustomer && (
          <Link to="/account" data-testid="account-link"
            className="w-9 h-9 rounded-full flex items-center justify-center nc-glass-icon hover:bg-black/5">
            <User className="w-[18px] h-[18px]" />
          </Link>
        )}
        <div className="w-px h-5 hidden xl:block nc-glass-divider" />
        <a href="tel:+919811742827" data-testid="header-call-btn"
          className="hidden xl:flex w-9 h-9 rounded-full items-center justify-center nc-glass-icon hover:bg-black/5">
          <PhoneCall className="w-[18px] h-[18px]" />
        </a>
        <a href="https://wa.me/919811742827?text=Hi%20NirvanaComfort!%20I%20have%20a%20question%20about%20your%20products."
          target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-btn"
          className="w-9 h-9 rounded-full flex items-center justify-center text-[#25D366] hover:text-[#5dfc9a] transition-colors">
          <WhatsAppIcon />
        </a>
      </div>
    </div>
  );
}
